import React, { useState, useEffect } from 'react';
import { useContent } from '../../../hooks/useContent';
import { useToast } from '../../../hooks/useToast';
import type { SocialLinks } from '../../../types';

const socialPlatforms: { key: keyof SocialLinks; name: string }[] = [
    { key: 'facebook', name: 'Facebook' },
    { key: 'instagram', name: 'Instagram' },
    { key: 'pinterest', name: 'Pinterest' },
    { key: 'threads', name: 'Threads' },
    { key: 'youtube', name: 'YouTube' },
    { key: 'linkedin', name: 'LinkedIn' },
];

export const ManageSocialLinks: React.FC = () => {
    const { socialLinks, updateSocialLinks } = useContent();
    const { showToast } = useToast();

    const [editableSocialLinks, setEditableSocialLinks] = useState(socialLinks);
    const [isSocialLoading, setIsSocialLoading] = useState(false);

    useEffect(() => {
        setEditableSocialLinks(socialLinks);
    }, [socialLinks]);

    const handleSocialLinkChange = (platform: keyof SocialLinks, value: string) => {
        setEditableSocialLinks(prev => ({ ...prev, [platform]: value }));
    };

    const handleSaveSocialLinks = async () => {
        if (!editableSocialLinks) return;
        setIsSocialLoading(true);
        try {
            await updateSocialLinks(editableSocialLinks);
            showToast('Social links saved successfully!');
        } catch (error) {
            console.error(error);
            showToast('Failed to save social links.');
        } finally {
            setIsSocialLoading(false);
        }
    };

    return (
        <div>
            <h3 className="text-md font-semibold text-indigo-800 mb-2">Social Media Profile URLs</h3>
            <div className="space-y-4 p-4 bg-white rounded-lg border">
                {socialPlatforms.map(platform => (
                    <div key={platform.key}>
                        <label htmlFor={`social-${platform.key}`} className="block text-sm font-medium text-gray-700 capitalize">{platform.name} URL</label>
                        <input
                            type="url"
                            id={`social-${platform.key}`}
                            value={editableSocialLinks?.[platform.key] || ''}
                            onChange={(e) => handleSocialLinkChange(platform.key, e.target.value)}
                            placeholder={`https://www.${platform.key}.com/your-profile`}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                ))}
            </div>
            <button onClick={handleSaveSocialLinks} disabled={isSocialLoading} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md disabled:opacity-50">{isSocialLoading ? 'Saving...' : 'Save Social Links'}</button>
        </div>
    );
};